import { UserState, PermissionMap } from "../types/permissions";
import { getRoutePermission } from "../config/routePermissions";

/**
 * List of public routes that don't require permissions
 */
const PUBLIC_ROUTES = ["logout", "welcome"];

/**
 * Check if user has permission for a specific permission key
 * @param user - User state from Redux
 * @param permissionKey - Permission key to check
 * @returns true if user has access, false otherwise
 */
export const hasPermission = (
  user: UserState | null,
  permissionKey?: string
): boolean => {
  // Check if this is a public route
  if (permissionKey && PUBLIC_ROUTES.includes(permissionKey?.toLowerCase())) {
    return true;
  }

  // ADMIN role has all permissions
  if (user?.role === "ADMIN") {
    return true;
  }

  // If no permission key specified, deny access for non-ADMIN
  if (!permissionKey) {
    return false;
  }

  // Check if the permission key exists and has a valid value
  const permissionValue = user?.permissions?.[permissionKey];

  // Permission must be explicitly set to READ_ONLY or EDITABLE
  // Empty string '', undefined, null, "NONE", or "null" all mean FORBIDDEN
  const hasAccess = Boolean(
    permissionValue &&
      permissionValue !== "NONE" &&
      permissionValue !== "null" &&
      permissionValue !== null &&
      permissionValue !== undefined &&
      permissionValue !== ""
  );

  return hasAccess;
};

/**
 * Check if user has access to a specific route
 * @param user - User state from Redux
 * @param path - Route path
 * @returns true if user has access, false otherwise
 */
export const hasRouteAccess = (
  user: UserState | null,
  path: string
): boolean => {
  const routeConfig = getRoutePermission(path);

  // ADMIN role has access to all routes
  if (user?.role === "ADMIN") {
    return true;
  }

  // Public routes are always accessible
  if (routeConfig?.isPublic) {
    return true;
  }

  // If route config is undefined or empty object, deny access for non-ADMIN
  if (!routeConfig || Object.keys(routeConfig).length === 0) {
    return false;
  }

  // Check permission-based access
  return hasPermission(user, routeConfig?.permission);
};

/**
 * Get permission value for a specific permission key
 * @param user - User state from Redux
 * @param permissionKey - Permission key to check
 * @returns Permission value (READ_ONLY, EDITABLE, or null)
 */
export const getPermissionLevel = (
  user: UserState | null,
  permissionKey: string
): string | null => {
  // ADMIN role has full access
  if (user?.role === "ADMIN") {
    return "EDITABLE";
  }

  const permissionValue = user?.permissions?.[permissionKey];

  if (
    permissionValue &&
    permissionValue !== "NONE" &&
    permissionValue !== "null" &&
    permissionValue !== null &&
    permissionValue !== undefined &&
    permissionValue !== ""
  ) {
    return permissionValue as string;
  }

  return null;
};

/**
 * Check if user has editable access (not just read-only)
 * @param user - User state from Redux
 * @param permissionKey - Permission key to check
 * @returns true if user has EDITABLE access, false otherwise
 */
export const hasEditableAccess = (
  user: UserState | null,
  permissionKey: string
): boolean => {
  return getPermissionLevel(user, permissionKey) === "EDITABLE";
};
